import { apiUrl } from "config/envConfig";
import http from "./httpService";
import {
  DEFAULT_PAGE,
  DEFAULT_ROWS_PER_PAGE,
} from "constants/paginationConstants";
import { fallbackValue } from "constants/fallbackConstant";
import dayjs from "dayjs";

const path = "/user-mgmt/admin/drivers";
const url = apiUrl;

/**
 * Fetch drivers list with pagination
 *
 * @param {number} pageSize   Number of records per page.
 * @param {number} pageNumber Current page number.
 * @param {string} fullName   Driver name filter.
 * @param {string} phoneNumber Driver phone number filter.
 * @param {string} city       City filter.
 * @param {array} status      Status filter array.
 * @param {boolean} descendingNameSort Sort by name.
 * @param {boolean} descendingAgeSort Sort by age.
 * @param {string} creationDate Creation date filter.
 * @param {boolean} descendingCreationDateSort Sort by creation date.
 * @param {boolean} descendingModificationDateSort Sort by modification date.
 * @param {number} driverType Driver type filter (1 for Regular, 2 for Expedited).
 * @returns {Promise<Object>} Paginated driver data.
 */
export const getDrivers = async (
  pageSize = DEFAULT_ROWS_PER_PAGE,
  pageNumber = 1,
  fullName = "",
  phoneNumber = "",
  city = "",
  status = [],
  descendingNameSort = null,
  descendingAgeSort = null,
  creationDate = null,
  descendingCreationDateSort = null,
  descendingModificationDateSort = null,
  driverType = null
) => {
  const params = new URLSearchParams({ pageSize, pageNumber });

  if (Array.isArray(status) && status.length > 0) {
    status.forEach((statusValue) => params.append("Status", statusValue));
  }

  if (fullName) params.append("FullName", fullName);
  if (phoneNumber) params.append("PhoneNumber", phoneNumber);
  if (city) params.append("City", city);
  if (descendingNameSort !== null)
    params.append("DescendingNameSort", descendingNameSort);
  if (descendingAgeSort !== null)
    params.append("DescendingAgeSort", descendingAgeSort);

  if (creationDate) {
    params.append("CreationDate", dayjs(creationDate).toISOString());
  }
  if (descendingCreationDateSort !== null)
    params.append("DescendingCreationDateSort", descendingCreationDateSort);
  if (descendingModificationDateSort !== null)
    params.append(
      "DescendingModificationDateSort",
      descendingModificationDateSort
    );
  
  if (driverType !== null && driverType !== undefined) {
    params.append("DriverType", driverType);
  }
  
  const {
    data: { data },
  } = await http.get(`${url}${path}?${params.toString()}`);

  return {
    page: data.page || [],
    totalRecords: data.totalRecords || 0,
  };
};
/**
 * Fetch detailed driver info
 *
 * @param {string} driverId The ID of the driver.
 * @returns {Promise<Object>} Driver details.
 */
export const getDriverDetails = async (driverId) => {
  const {
    data: { data },
  } = await http.get(`${url}${path}/detailed?driverId=${driverId}`);
  return data;
};

/**
 * Approve a driver
 *
 * @param {string} driverId The ID of the driver.
 * @returns {Promise<Object>} Response data.
 */
export const approveDriver = async (driverId) => {
  const {
    data: { data },
  } = await http.patch(`${url}${path}/approve?driverId=${driverId}`);
  return data;
};

/**
 * Get driver-specific orders with optional filters.
 *
 * @param {string} driverId - ID of the driver.
 * @param {number} pageSize - Number of rows per page.
 * @param {number} pageNumber - Current page number.
 * @param {number | null} status - Status value (single number)
 * @param {string | null} senderName - Filter by sender's name.
 * @param {string | null} receiverName - Filter by receiver's name.
 * @param {string | null} orderNumber - Filter by order number.
 * @param {string | null} pickupDate - Filter by pickup date (ISO string).
 * @param {string | null} dropoffDate - Filter by dropoff date (ISO string).
 * @returns {Promise<Object>} Filtered driver order list.
 */
export const getDriverOrders = async (
  driverId,
  pageSize = DEFAULT_ROWS_PER_PAGE,
  pageNumber = DEFAULT_PAGE,
  status = null,
  senderName = null,
  receiverName = null,
  orderNumber = null,
  pickupDate = null,
  dropoffDate = null,
  pickUpLocation = null,
  dropOffLocation = null
) => {
  const body = {
    pageSize,
    pageNumber,
  };

  if (status !== null && status !== undefined && status !== 0) {
    body.status = Number(status);
  }

  if (senderName) {
    body.senderName = senderName;
  }

  if (receiverName) {
    body.receiverName = receiverName;
  }

  if (orderNumber) {
    body.orderId = orderNumber;
  }

  if (pickupDate) {
    body.pickupDate = new Date(pickupDate).toISOString();
  }

  if (dropoffDate) {
    body.dropoffDate = new Date(dropoffDate).toISOString();
  }
  if (pickUpLocation) {
    body.pickUpLocation = pickUpLocation;
  }

  if (dropOffLocation) {
    body.dropOffLocation = dropOffLocation;
  }

  const {
    data: { data },
  } = await http.post(
    `${apiUrl}/order-mgmt/admin/driver/${driverId}/orders`,
    body
  );

  return {
    page: data.page.map((order) => ({
      id: order.id,
      orderNumber: `#${order.orderNumber}`,
      senderName: order.senderName,
      receiverName: order.receiverName || fallbackValue,
      prefeeredPickUpDate: order.prefeeredPickUpDate || null,
      prefeeredDeliveryDate: order.prefeeredDeliveryDate || null,
      status: order.status,
      pickUpLocation: order.pickUpLocation
        ? ` ${order.pickUpLocation.area || ""}`
        : fallbackValue,
      dropOffLocation: order.dropOffLocation
        ? ` ${order.dropOffLocation.area || ""}`
        : fallbackValue,
    })),
    totalRecords: data.totalRecords || 0,
  };
};

export const getDriverOrderDetails = async (
  driverId,
  pageSize = 10,
  pageNumber = 1
) => {
  const {
    data: { data },
  } = await http.get(`/order-mgmt/Admin/Driver/${driverId}/Route`, {
    params: {
      PageSize: pageSize,
      PageNumber: pageNumber,
    },
  });

  return {
    page: data.page.map((order) => ({
      id: order.id,
      orderNumber: `#${order.orderNumber}`,
      senderName: order.senderName,
      receiverName: order.receiverName || fallbackValue,
      prefeeredPickUpDate: order.prefeeredPickUpDate || null,
      prefeeredDeliveryDate: order.prefeeredDeliveryDate || null,
      status: order.status,
      pickUpLocation: order.pickUpLocation?.area || fallbackValue,
      dropOffLocation: order.dropOffLocation?.area || fallbackValue,
    })),
    totalRecords: data.totalRecords || 0,
  };
};

/**
 * Fetch a single driver request by ID
 *
 * @param {string} requestId The ID of the driver change request.
 * @returns {Promise<Object>} Driver request data.
 */
export const getDriverRequestById = async (requestId) => {
  const {
    data: { data },
  } = await http.get(
    `${apiUrl}/order-mgmt/admin/Driver/change-requests/${requestId}`
  );
  return data;
};

export const getActiveDrivers = async () => {
  const {
    data: { data },
  } = await http.get(`${apiUrl}/location-mgmt/admin/driver/GetActiveDrives`);
  return data.page || [];
};

/**
 * Change a driver's type (Regular / Expedited / Both)
 * @param {string} driverId
 * @param {number} newType
 */
export const changeDriverType = async (driverId, newType) => {
  // The backend expects a raw enum value (number) in the request body,
  // not an object. Send the numeric value directly so model binding can
  // convert it to the DriverTypeEnum.
  const value = Number(newType);
  // Send the numeric value as a JSON payload and explicitly set Content-Type
  // application/json so ASP.NET model binding can convert it to the enum.
  const {
    data: { data },
  } = await http.patch(
    `${apiUrl}/user-mgmt/admin/drivers/drivertype?driverId=${driverId}`,
    JSON.stringify(value),
    {
      headers: {
        "Content-Type": "application/json",
      },
    }
  );
  return data;
};
